import React from 'react';
import { FormControl, RadioGroup, FormControlLabel, Radio, Collapse, Typography, Alert } from '@mui/material';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PaymentScheduleTable from '@acorn-portal/shared/src/components/display/payment-schedule-table';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { PaymentInstallmentsMessageModel } from '@acorn-portal/shared/src/services/api';

export type PaymentPlanOptionsType = 'PayInFull' | 'SpTwo' | 'SpThree';

interface PaymentOption {
	paymentPlanType: PaymentPlanOptionsType;
	installments: PaymentInstallmentsMessageModel[];
	fee?: number;
}

interface PaymentOptionsProps {
	paymentCollection: PaymentOption[];
	selectedPaymentType: PaymentPlanOptionsType;
	onChange: (event: React.ChangeEvent<HTMLInputElement>, value: PaymentPlanOptionsType) => void;
}

const PaymentOptions: React.FC<PaymentOptionsProps> = ({ paymentCollection, selectedPaymentType, onChange }) => {
	const latePaymentFee = Number(paymentCollection[0].fee ?? 0);
	const renderLabel = (installments: PaymentInstallmentsMessageModel[]) => {
		const firstInstallmentAmount = Number(installments[0].amount);
		if (installments.length === 1) {
			return `Pay in full ${formatCurrency(firstInstallmentAmount)}`;
		} else if (firstInstallmentAmount <= latePaymentFee + 0.02) {
			return `Split into ${installments.length} Payments`;
		} else {
			return `Split into ${installments.length} payments of ${formatCurrency(firstInstallmentAmount)} each`;
		}
	};

	const showInfoAlert = () =>
		paymentCollection.some(
			(option) => option.installments.length > 1 && Number(option.installments[0].amount) <= latePaymentFee,
		);

	return (
		<FormControl component='fieldset' sx={{ width: '100%', px: { lg: 3, xs: 0 } }}>
			<RadioGroup
				aria-label='splitPayments'
				name='splitPayments'
				value={selectedPaymentType}
				onChange={(e, value) => onChange(e, value as PaymentPlanOptionsType)}
			>
				{paymentCollection.map(({ paymentPlanType, installments }, index) => (
					<React.Fragment key={`payment-plan-${index}`}>
						<FormControlLabel
							value={paymentPlanType}
							sx={{ textAlign: 'left' }}
							control={
								<Radio
									icon={<CircleOutlinedIcon />}
									checkedIcon={<CheckCircle />}
									sx={{ '& .MuiSvgIcon-root': { fontSize: '3rem' } }}
								/>
							}
							label={renderLabel(installments)}
						/>
						{installments.length > 1 && (
							<Collapse in={selectedPaymentType === paymentPlanType}>
								<Typography variant='h6' sx={{ mb: 2, textAlign: 'left' }}>
									Payment Schedule
								</Typography>
								{showInfoAlert() && (
									<Alert severity='info' sx={{ mb: 2 }}>
										Please note that you need to cover the full late payment fee of {formatCurrency(latePaymentFee)}{' '}
										with your first payment.
									</Alert>
								)}
								<PaymentScheduleTable installments={installments} />
							</Collapse>
						)}
					</React.Fragment>
				))}
			</RadioGroup>
		</FormControl>
	);
};

export default PaymentOptions;
